import { useState } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { ScopeReveal } from "@/components/itsm-config/scope-reveal";
import {
  ServiceNowCloseFieldsSection,
  ServiceNowRequesterLocationSection,
} from "@/components/itsm-config/provider-specific-sections";
import { useGeneralMappingSave } from "@/hooks/use-general-mapping-save";
import { findRule } from "@/lib/itsm-config/general-mapping";
import type {
  GeneralMappingRule,
  ProviderConfig,
  ItsmProviderFamily,
} from "@/lib/types/itsm-config";

function JsonEditor({
  value,
  onChange,
}: {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown> | null) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);

  const handleChange = (raw: string) => {
    setText(raw);
    if (!raw.trim()) {
      setError(null);
      onChange(null);
      return;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
        setError("Must be a JSON object");
        return;
      }
      setError(null);
      onChange(parsed as Record<string, unknown>);
    } catch {
      setError("Invalid JSON");
    }
  };

  return (
    <div className="space-y-1">
      <Textarea
        value={text}
        onChange={(e) => { handleChange(e.target.value); }}
        className="font-mono text-xs min-h-[120px]"
        placeholder='{"customfield_10001": "IT Service Desk"}'
      />
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}

interface Props {
  tenantId: string;
  credentialId: string;
  provider: string;
  family: ItsmProviderFamily;
  providerConfig: ProviderConfig;
}

/**
 * Card 5 — Field rules. Static field values set on create (custom_field_values,
 * per scope), plus the provider-specific field mechanics (Jira export field
 * overrides / ServiceNow close fields). Skipped ("excluded") fields are set
 * per routing rule in the "Where tickets go" card — cross-referenced here.
 */
export function FieldRulesSection({
  tenantId,
  credentialId,
  provider,
  family,
  providerConfig,
}: Props) {
  const rules = providerConfig.general_mapping ?? [];
  const save = useGeneralMappingSave();
  const baseProps = { tenantId, credentialId, provider, providerConfig };

  const hasField = (r: GeneralMappingRule) =>
    Boolean(r.custom_field_values && Object.keys(r.custom_field_values).length);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Field rules</CardTitle>
        <CardDescription>
          Set fixed field values on every ticket Harmony creates. To skip a
          field on create, use "Excluded fields" in the "Where tickets go" card.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <p className="text-sm font-medium">Fixed field values on create</p>
          <ScopeReveal
            family={family}
            tenantId={tenantId}
            credentialId={credentialId}
            rules={rules}
            hasField={hasField}
            defaultNote="Set on every ticket created in any project."
            onClearScope={(match) => { save(match, { custom_field_values: null }); }}
            renderEditor={(match) => (
              <JsonEditor
                value={findRule(rules, match)?.custom_field_values ?? {}}
                onChange={(m) => {
                  save(match, {
                    custom_field_values: m && Object.keys(m).length ? m : null,
                  });
                }}
              />
            )}
          />
        </div>

        {family === "servicenow" && (
          <>
            <Separator />
            <ServiceNowRequesterLocationSection {...baseProps} />
            <Separator />
            <ServiceNowCloseFieldsSection {...baseProps} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
